import { useCallback, useState } from "react";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { Dialog } from "primereact/dialog";
import TabHeader from "../common/TabHeader";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { useSelector } from "react-redux";
import CheckboxInput from "../common/CheckBoxInput";
import HasRequiredAuthority from "../dependencies/HasRequiredAuthority";
import { AUTHORITIES } from "../../constants";
import { getWriteableDate, toAppDate } from "../../utils";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";

export default function DialogEditBatch({
    visible,
    setBatches,
    closeDialog,
    id,
    title,
    description,
    branch_id,
    start_date,
    end_date,
    active,
}) {
    const { requestAPI, showToast } = useAppContext();
    const { branches = [] } = useSelector((state) => state.stateTemplateConfig?.global);

    const [batch, setBatch] = useState({
        title,
        description,
        branch_id,
        start_date: toAppDate(start_date),
        end_date: toAppDate(end_date),
        active,
    });
    const [loading, setLoading] = useState();

    const editBatch = useCallback(() => {
        requestAPI({
            requestPath: `batches`,
            requestMethod: "PATCH",
            requestPostBody: {
                id,
                title: batch?.title,
                description: batch?.description || null,
                branch_id: batch?.branch_id || null,
                start_date: batch?.start_date ? getWriteableDate({ date: batch.start_date, removeTime: true }) : null,
                end_date: batch?.end_date ? getWriteableDate({ date: batch.end_date, removeTime: true }) : null,
                active: !!batch?.active,
            },
            setLoading: setLoading,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Batch !", life: 2000 }),
            onResponseReceieved: (updated, responseCode) => {
                if (updated && responseCode === 200) {
                    showToast({ severity: "success", summary: "Updated", detail: "Batch Updated", life: 1000 });
                    setBatches((prev) => prev?.map((item) => (item?.id === id ? updated : item)));
                    closeDialog();
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Batch !", life: 2000 });
                }
            },
        });
    }, [batch, closeDialog, id, requestAPI, setBatches, showToast]);

    return (
        <Dialog pt={{ content: { className: "overflow-y-auto" } }} header="Edit Batch" visible={visible} className="w-11" onHide={closeDialog}>
            <TabHeader className="pt-3" title="Edit Batch" />

            <FloatLabel className="mt-5">
                <InputText
                    value={batch?.title || ""}
                    id="title"
                    className="w-full"
                    onChange={(e) => setBatch((prev) => ({ ...prev, title: e.target.value }))}
                    disabled={loading}
                    pt={{ root: { className: TEXT_NORMAL } }}
                />
                <label htmlFor="title">Title</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <InputTextarea
                    value={batch?.description || ""}
                    id="description"
                    rows={3}
                    className="w-full"
                    onChange={(e) => setBatch((prev) => ({ ...prev, description: e.target.value || null }))}
                    disabled={loading}
                    pt={{ root: { className: TEXT_NORMAL } }}
                />
                <label htmlFor="description">Description</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Dropdown
                    className="w-full"
                    value={branches?.find(({ id }) => id === batch?.branch_id)}
                    inputId="branch"
                    options={branches}
                    optionLabel="title"
                    showClear
                    onChange={(e) => setBatch((prev) => ({ ...prev, branch_id: e.value?.id || null }))}
                    disabled={loading}
                    pt={{ input: { className: TEXT_NORMAL }, item: { className: TEXT_NORMAL } }}
                />
                <label htmlFor="branch">Branch</label>
            </FloatLabel>

            <div className="flex gap-2 mt-5">
                <FloatLabel className="flex-1">
                    <Calendar
                        className="w-full"
                        inputId="start_date"
                        value={batch?.start_date}
                        onChange={(e) => setBatch((prev) => ({ ...prev, start_date: e.value }))}
                        disabled={loading}
                        dateFormat="dd/mm/yy"
                    />
                    <label htmlFor="start_date" className={TEXT_SMALL}>
                        Start Date
                    </label>
                </FloatLabel>
                <FloatLabel className="flex-1">
                    <Calendar
                        className="w-full"
                        inputId="end_date"
                        value={batch?.end_date}
                        onChange={(e) => setBatch((prev) => ({ ...prev, end_date: e.value }))}
                        disabled={loading}
                        dateFormat="dd/mm/yy"
                        minDate={batch?.start_date}
                    />
                    <label htmlFor="end_date" className={TEXT_SMALL}>
                        End Date
                    </label>
                </FloatLabel>
            </div>

            <CheckboxInput
                className="mt-3"
                label="Active"
                checked={!!batch?.active}
                onChange={(checked) => setBatch((prev) => ({ ...prev, active: checked }))}
            />

            <HasRequiredAuthority requiredAuthority={AUTHORITIES.UPDATE_BATCH}>
                <Button className="mt-3" label="Update Batch" severity="warning" loading={loading} onClick={editBatch} disabled={!batch?.title} />
            </HasRequiredAuthority>
        </Dialog>
    );
}
